import React, { useState, useRef } from 'react';
import { useToast } from '../../Admissionform/ToastManager';
import '../../../styles/MappedStudentsActions.css';

const MappedStudentsActions = ({ jobReqId, currentStatus, acknowledgementStatus, studentCount, branch, studentName,
    placementInterest, fromDate, toDate }) => {
    const [downloading, setDownloading] = useState(false);
    const [downloadType, setDownloadType] = useState(null);
    const { showSuccess, showError, showInfo } = useToast();

    // Refs to prevent multiple clicks
    const excelDownloadInProgress = useRef(false);
    const resumeDownloadInProgress = useRef(false);

    // ----------------------------------
    // Download Excel
    // ----------------------------------
    const handleDownloadExcel = async () => {
        if (excelDownloadInProgress.current || downloading) return;

        if (studentCount === 0) {
            showInfo('No students available to export');
            return;
        }

        excelDownloadInProgress.current = true;
        setDownloading(true);
        setDownloadType('excel');

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                showError('Authentication required');
                return;
            }

            const queryParams = new URLSearchParams();
            if (currentStatus && currentStatus !== 'all') {
                queryParams.append('status', currentStatus);
            }
            if (acknowledgementStatus?.length > 0) {
                queryParams.append(
                    'acknowledgementStatus',
                    acknowledgementStatus.join(',')
                );
            }
            if (branch?.length > 0) {
                queryParams.append(
                    'branch',
                    branch.join(',')
                );
            }
            if (studentName) {
                queryParams.append('studentName', studentName);
            }

            if (placementInterest?.length > 0) {
                queryParams.append(
                    'placementInterest',
                    placementInterest.join(',')
                );
            }

            if (fromDate) {
                queryParams.append('fromDate', fromDate);
            }

            if (toDate) {
                queryParams.append('toDate', toDate);
            }

            const url = `${process.env.REACT_APP_API_URL}/company/${jobReqId}/export-excel?${queryParams}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to download Excel');
            }

            // Default filename fallback
            let filename = `Mapped_Students_${new Date().toISOString().split('T')[0]}.xlsx`;

            // Extract filename from header safely
            const contentDisposition = response.headers.get('Content-Disposition');
            if (contentDisposition) {
                const match = contentDisposition.match(
                    /filename\*?=(?:UTF-8''|")?([^";\n]+)/i
                );
                if (match && match[1]) {
                    filename = decodeURIComponent(match[1]).trim();
                    // Remove trailing underscores or dots (Windows-safe)
                    filename = filename.replace(/[_.]+$/, '');
                }
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

            showSuccess('Excel file downloaded successfully');
        } catch (error) {
            console.error('Error downloading Excel:', error);
            showError(error.message || 'Failed to download Excel file');
        } finally {
            setDownloading(false);
            setDownloadType(null);
            excelDownloadInProgress.current = false;
        }
    };

    // ----------------------------------
    // Download Resumes (ZIP)
    // ----------------------------------
    const handleDownloadResumes = async () => {
        if (resumeDownloadInProgress.current || downloading) return;

        if (studentCount === 0) {
            showInfo('No students available to download resumes');
            return;
        }

        resumeDownloadInProgress.current = true;
        setDownloading(true);
        setDownloadType('resumes');

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                showError('Authentication required');
                return;
            }

            const queryParams = new URLSearchParams();
            if (currentStatus && currentStatus !== 'all') {
                queryParams.append('status', currentStatus);
            }
            if (acknowledgementStatus?.length > 0) {
                queryParams.append(
                    'acknowledgementStatus',
                    acknowledgementStatus.join(',')
                );
            }
            if (branch?.length > 0) {
                queryParams.append(
                    'branch',
                    branch.join(',')
                );
            }
            if (studentName) {
                queryParams.append('studentName', studentName);
            }

            if (placementInterest?.length > 0) {
                queryParams.append(
                    'placementInterest',
                    placementInterest.join(',')
                );
            }

            if (fromDate) {
                queryParams.append('fromDate', fromDate);
            }

            if (toDate) {
                queryParams.append('toDate', toDate);
            }

            const url = `${process.env.REACT_APP_API_URL}/company/${jobReqId}/download-resumes?${queryParams}`;

            showInfo('Preparing resumes for download... This may take a moment.');

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to download resumes');
            }

            let filename = `Mapped_Students_Resumes_${new Date().toISOString().split('T')[0]}.zip`;

            const contentDisposition = response.headers.get('Content-Disposition');
            if (contentDisposition) {
                const match = contentDisposition.match(
                    /filename\*?=(?:UTF-8''|")?([^";\n]+)/i
                );
                if (match && match[1]) {
                    filename = decodeURIComponent(match[1]).trim();
                    filename = filename.replace(/[_.]+$/, '');
                }
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

            showSuccess('Resumes downloaded successfully');
        } catch (error) {
            console.error('Error downloading resumes:', error);
            showError(error.message || 'Failed to download resumes');
        } finally {
            setDownloading(false);
            setDownloadType(null);
            resumeDownloadInProgress.current = false;
        }
    };

    return (
        <div className="mapped-students-actions">
            <button
                onClick={handleDownloadExcel}
                disabled={downloading || studentCount === 0}
                className={`action-btn excel-btn ${downloading && downloadType === 'excel' ? 'downloading' : ''}`}
                title="Download Excel"
                type="button"
            >
                {downloading && downloadType === 'excel' ? (
                    <>
                        <span className="btn-spinner"></span>
                        <span>Downloading...</span>
                    </>
                ) : (
                    <>
                        <span>Download Excel</span>
                    </>
                )}
            </button>

            <button
                onClick={handleDownloadResumes}
                disabled={downloading || studentCount === 0}
                className={`action-btn resume-btn ${downloading && downloadType === 'resumes' ? 'downloading' : ''}`}
                title="Download Resumes (ZIP)"
                type="button"
            >
                {downloading && downloadType === 'resumes' ? (
                    <>
                        <span className="btn-spinner"></span>
                        <span>Downloading...</span>
                    </>
                ) : (
                    <>
                        <span>Download Resumes</span>
                    </>
                )}
            </button>
        </div>
    );
};

export default MappedStudentsActions;
